import React from "react";
import { Plus, Trash2, Clock } from "lucide-react";
import { DEFAULT_TIMINGS, toDisplay } from "@/lib/timings";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_IDX = [1, 2, 3, 4, 5, 6, 0];

export default function TimingsEditor({ value, onChange }) {
  const rows = Array.isArray(value) && value.length ? value : DEFAULT_TIMINGS;
  const update = (i, patch) => onChange(rows.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const toggleDay = (i, d) => {
    const days = rows[i].days.includes(d) ? rows[i].days.filter((x) => x !== d) : [...rows[i].days, d];
    update(i, { days });
  };
  const add = () => onChange([...rows, { days: [], start: "10:00", end: "13:00", note: "" }]);
  const remove = (i) => onChange(rows.filter((_, k) => k !== i));
  const preview = toDisplay(rows);

  return (
    <div data-testid="timings-editor">
      <div className="mb-1.5 flex items-center gap-2"><Clock className="h-4 w-4 text-terracotta" /><label className="block text-sm font-medium text-charcoal">Clinic Timings</label></div>
      <p className="mb-3 text-xs text-taupe">Drives the timings shown on Contact, Appointment and Patient Experience pages, plus the live "Open now" indicator (IST).</p>
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={i} data-testid={`timing-row-${i}`} className="rounded-2xl border border-soft-border bg-white p-4">
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map((d, k) => {
                const idx = DAY_IDX[k];
                const on = r.days.includes(idx);
                return (
                  <button key={d} type="button" data-testid={`timing-${i}-day-${d.toLowerCase()}`} onClick={() => toggleDay(i, idx)} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${on ? "bg-terracotta text-cream" : "bg-blush/60 text-taupe hover:bg-blush"}`}>{d}</button>
                );
              })}
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-[auto_auto_1fr_auto] sm:items-center">
              <label className="flex items-center gap-2 text-xs text-taupe">From <input type="time" data-testid={`timing-${i}-start`} value={r.start} onChange={(e) => update(i, { start: e.target.value })} className="rounded-lg border border-soft-border p-2 text-sm text-charcoal outline-none focus:border-terracotta" /></label>
              <label className="flex items-center gap-2 text-xs text-taupe">To <input type="time" data-testid={`timing-${i}-end`} value={r.end} onChange={(e) => update(i, { end: e.target.value })} className="rounded-lg border border-soft-border p-2 text-sm text-charcoal outline-none focus:border-terracotta" /></label>
              <input data-testid={`timing-${i}-note`} placeholder="Note (optional, e.g. Evening consultation)" value={r.note || ""} onChange={(e) => update(i, { note: e.target.value })} className="rounded-lg border border-soft-border p-2 text-sm outline-none focus:border-terracotta" />
              <button type="button" aria-label="Remove slot" data-testid={`timing-${i}-remove`} onClick={() => remove(i)} className="inline-flex h-9 w-9 items-center justify-center rounded-full text-taupe hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <button type="button" data-testid="timing-add" onClick={add} className="mt-3 inline-flex items-center gap-2 rounded-full border border-terracotta/40 px-4 py-2 text-xs font-semibold text-terracotta hover:bg-blush"><Plus className="h-3.5 w-3.5" /> Add time slot</button>

      <div className="mt-4 rounded-2xl bg-blush/40 p-4" data-testid="timings-preview">
        <p className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-terracotta">Preview</p>
        <ul className="mt-2 space-y-1 text-sm">
          {preview.map((t, i) => <li key={i} className="flex justify-between gap-4"><span className="text-taupe">{t.day}</span><span className="font-medium text-charcoal">{t.time}</span></li>)}
          {preview.length === 0 && <li className="text-taupe">No valid slots — select at least one day per slot.</li>}
        </ul>
      </div>
    </div>
  );
}
